import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Canvas } from "@cohesive/api-client";
import { getCanvasHeroImageUrl } from "@cohesive/api-client";
import { formatDistanceToNow } from "date-fns";
import { Frame, Image, Users } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useThemeContext } from "../../../context/ThemeContext";
import {
  gridCardFooterMetaRowClassName,
  gridCardFooterSubtitleClassName,
  gridCardFooterTitleClassName,
  gridCardPreviewPlaceholderIconClassName,
} from "../constants";
import {
  WorkspaceCardFooterTitleRow,
  WorkspaceCardFooterTypeIcon,
} from "../WorkspaceCardFooterTypeIcon";
import { WorkspaceCardStarButton } from "../WorkspaceCardStarButton";
import { WorkspaceGridCard } from "../WorkspaceGridCard";

export interface CanvasOwner {
  id: string;
  name?: string;
  picture_url?: string;
}

export interface CanvasCardProps {
  canvas: Canvas;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Right-click handler forwarded to the card root — wire to the shared
   * `useRailRowContextMenu` controller so right-click opens the same menu the
   * kebab does, at the cursor.
   */
  onContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Registry-backed actions menu rendered in the footer (kebab + right-click).
   * Injected by the app so the card stays free of imperative action props; omit
   * to render a card with no menu (e.g. the mobile open-tab switcher).
   */
  menuSlot?: React.ReactNode;
  /** Favorite state + toggle for the on-card corner star. Omit `onToggleStar`
   *  to hide the star. */
  onToggleStar?: (canvas: Canvas) => void;
  isFavorite?: boolean;
  isSelected?: boolean;
  owner?: CanvasOwner;
  className?: string;
  style?: React.CSSProperties;
}

export const CanvasCard: React.FC<CanvasCardProps> = ({
  canvas,
  onClick,
  onDoubleClick,
  onContextMenu,
  menuSlot,
  onToggleStar,
  isFavorite = false,
  isSelected = false,
  owner,
  className,
  style,
}) => {
  const [imageError, setImageError] = useState(false);

  const editedTime = formatDistanceToNow(new Date(canvas.updated_at), {
    addSuffix: true,
  }).replace(/^about /, "");

  const { mode } = useThemeContext();
  const heroImageUrl = getCanvasHeroImageUrl(canvas, mode);
  const hasHeroImage = heroImageUrl && !imageError;

  useEffect(() => {
    setImageError(false);
  }, [heroImageUrl]);

  const preview = (
    <>
      <div className="absolute inset-0 flex items-center justify-center bg-surface-card">
        {hasHeroImage ? (
          <img
            src={heroImageUrl}
            alt={canvas.name || "Workspace preview"}
            // draggable=false: a native <img> drag advertises "Files" and
            // lights up upload drop zones; cards drag via dnd-kit instead.
            draggable={false}
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <Image
            className={gridCardPreviewPlaceholderIconClassName}
            strokeWidth={1.75}
            aria-hidden
          />
        )}
      </div>
      {onToggleStar && (
        <WorkspaceCardStarButton
          isFavorite={isFavorite}
          onToggle={() => onToggleStar(canvas)}
        />
      )}
    </>
  );

  const footer = (
    <>
      <div className="min-w-0 flex-1">
        <WorkspaceCardFooterTitleRow
          icon={<WorkspaceCardFooterTypeIcon icon={Frame} />}
        >
          <p className={gridCardFooterTitleClassName}>
            {canvas.name || "Untitled"}
          </p>
        </WorkspaceCardFooterTitleRow>
        <div
          className={cn(
            gridCardFooterMetaRowClassName,
            "flex items-center gap-1.5",
          )}
        >
          <p className={gridCardFooterSubtitleClassName}>{editedTime}</p>
          {owner && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  role="img"
                  aria-label={`Owner: ${owner.name || "Unknown"}`}
                  className="inline-flex h-3.5 w-3.5 items-center justify-center text-foreground"
                >
                  <Users className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{owner.name || "Unknown"}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {menuSlot && <div className="shrink-0">{menuSlot}</div>}
    </>
  );

  const canvasLabel = canvas.name || "Untitled canvas";

  return (
    <WorkspaceGridCard
      aria-label={canvasLabel}
      aria-selected={isSelected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      preview={preview}
      footer={footer}
      isSelected={isSelected}
      interactive={onClick != null || onDoubleClick != null}
      className={className}
      style={style}
    />
  );
};
